import { useCallback, useEffect, useMemo, useState } from "react";
import type { AdToken } from "./adTokens";
import {
    adTokenNotes,
    containsAdTokenSyntax,
    replaceAliasWithCanonical,
    scanAdTokens,
    type AdTokenNote,
    type AdTokenRange,
} from "./adTokenScan";
import { useAdTokens, type AdTokenStatus } from "./useAdTokens";

/**
 * The live ad token check for one text value, with no UI in it (MPN-1930).
 *
 * Whatever edits the text -- an ad copy textarea now, a Create Studio text layer's input
 * later (MPN-1933) -- passes its value in and renders what comes back however it renders
 * things. The vocabulary is the page's one shared read (useAdTokens), so any number of
 * fields calling this cost one request.
 */

/** A stable identity, so a caller that passes nothing does not re-memoise every render. */
const NO_EXTRA: AdToken[] = [];

/** How long the text has to sit still before a new note appears. */
export const AD_TOKEN_SETTLE_MS = 300;

/** New text after a fix, and where the caret belongs in it: just after the new token. */
export type AdTokenEdit = { value: string; caret: number };

export type AdTokenCheck = {
    status: AdTokenStatus;
    /** The vocabulary, for anything that also offers it (the picker). Empty unless ready. */
    tokens: AdToken[];
    /**
     * Every token in the value as it is right now, classified. Not debounced: a highlight
     * has to sit on the text as it is. Empty unless the vocabulary is ready.
     */
    ranges: AdTokenRange[];
    /**
     * One note per token, for the value as of the writer's last pause. A note for a token
     * that has since gone is dropped at once rather than a pause later.
     */
    notes: AdTokenNote[];
    /**
     * The value has something token-shaped in it and there is no vocabulary to check it
     * against -- the read failed, or the registry is empty. No notes are given then: against
     * nothing, every bracket would be unknown.
     */
    unavailable: boolean;
    /**
     * The value with the first use of this alias rewritten to its canonical, or null if it
     * has no such alias any more. Defaults to the value passed in; pass the field's own
     * current text when it may have moved on since. Returns the edit -- applying it is the
     * caller's, through whatever path a keystroke takes.
     */
    applyAliasFix: (target: Pick<AdTokenNote, "normalized">, text?: string) => AdTokenEdit | null;
};

/** The value as of the last pause. Starts settled, so saved text is checked on first render. */
function useSettledValue(value: string, delay: number): string {
    const [settled, setSettled] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return settled;
}

/**
 * `extra` adds tokens the registry does not hold to this field's vocabulary.
 *
 * Empty for ad copy, which is the registry's vocabulary exactly. The Email & SMS screens
 * pass the per-recipient fields -- `[FIRST NAME]`, `[LAST NAME]` -- which `render.py` fills
 * from the audience member on a send. They are deliberately NOT registered: `/ad-tokens/`
 * serves every definition to every picker, and a token nothing can fill in an ad has no
 * business being offered to a copywriter. They are merge fields on a sequence and nowhere
 * else, so the screen that sends sequences is where they belong.
 */
export function useAdTokenCheck(value: string, extra: AdToken[] = NO_EXTRA): AdTokenCheck {
    const { tokens: registry, status } = useAdTokens();
    const tokens = useMemo(
        () => (extra.length ? [...registry, ...extra] : registry),
        [registry, extra],
    );
    const ready = status === "ready";
    const settled = useSettledValue(value, AD_TOKEN_SETTLE_MS);

    const ranges = useMemo(() => (ready ? scanAdTokens(value, tokens) : []), [ready, value, tokens]);
    const settledNotes = useMemo(
        () => (ready ? adTokenNotes(scanAdTokens(settled, tokens)) : []),
        [ready, settled, tokens],
    );
    // A new note waits for the writer to pause, or `[S`, `[SC`, `[SCH` would each flash red
    // on the way to `[SCHOOL NAME]`. A fixed one goes at once: a warning that outlives its
    // typo reads as a check that is wrong.
    const notes = useMemo(() => {
        const present = new Set(ranges.map((r) => r.normalized));
        return settledNotes.filter((note) => present.has(note.normalized));
    }, [ranges, settledNotes]);

    const unavailable = status === "unavailable" && containsAdTokenSyntax(value);

    const applyAliasFix = useCallback(
        (target: Pick<AdTokenNote, "normalized">, text: string = value) =>
            ready ? replaceAliasWithCanonical(text, tokens, target.normalized) : null,
        [ready, tokens, value],
    );

    return { status, tokens: ready ? tokens : [], ranges, notes, unavailable, applyAliasFix };
}
